import { ToolsColumn } from "components/features/tools/ToolsColumn"
import { ToolsContainer } from "components/features/tools/ToolsContainer"
import { CalculationOutput } from "components/ui/display/CalculationOutput"
import { Input } from "components/ui/inputs/Input"
import { useErrors } from "providers/ErrorsProvider"
import { useEffect, useState } from "react"
import { calculationLimits } from "store/calculation.config"
import styled from "styled-components"
import { calculateLefturn } from "./Leftturn.calculate"
import { ReactComponent as LeftTurnCrossing } from 'assets/img/leftTurnCrossing.svg';
import { ContentContainer } from "components/ui/display/ContentContainer"
import { DropdownMenu, DropdownOption } from "components/ui/inputs/DropdownMenu"
import { MaterialIcon } from "components/ui/shared/MaterialIcon"

export const Leftturn = () => {

    const {setError, unsetError} = useErrors();
    const [rawInput, setRawInput] = useState({
        speedlimit: '',
        slope: '',
        trafficThrough: '',
        trafficTurning: '',
        trafficOncoming: '',
        heavyPercentage: '',
        designVehicle: 'lastebil'
    });

    const [calculationInput, setCalculationInput] = useState({
        speedlimit: '',
        slope: '',
        trafficThrough: '',
        trafficTurning: '',
        trafficOncoming: '',
        heavyPercentage: '',
        designVehicle: 'lastebil'
    });

    const [calculatedState, setCalculatedState] = useState({
        input: null,
        derived: null,
        output: null,
        errorsToSet: [],
        errorsToUnset: []
    });

    useEffect(() => {
        const context = calculateLefturn(calculationInput);
        if(context.wasNormalized)
        {
            setRawInput(context.normalizedInput);
            setCalculationInput(prev => ({
                ...prev,
                ...Object.fromEntries(
                    Object.entries(context.normalizedInput).map(([key, value]) => [
                    key,
                    typeof value === 'number' ? String(value) : value,
                    ])
                ),
            }));
        }
        
        setCalculatedState({
            input: context.input,
            derived: context.derived,
            output: context.output
        });

        context.errorsToSet.forEach(setError);
        context.errorsToUnset.forEach(error => {
            if(!context.errorsToSet.includes(error)) unsetError(error);
        });
        
    }, [calculationInput, setError, unsetError]);

    return (
        <StyledLeftturn>
            <ToolsColumn className='inputColumn'>
                <ToolsContainer title='Inndata'>
                    <DropdownMenu
                        label="Dimensjonerende kjøretøy"
                        defaultValue={null}
                        defaultIcon={<MaterialIcon name='traffic_jam' weight={300}/>}
                        defaultSelected={calculationInput.designVehicle}
                        onSelect={(e) => {setCalculationInput(prev => ({...prev, designVehicle: e}))}}
                    >
                        <DropdownOption value="lastebil" name="Lastebil"/>
                        <DropdownOption value='vogntog' name='Vogntog'/>
                        <DropdownOption value="modulvogntog" name="Modulvogntog"/>
                    </DropdownMenu>
                    <Input
                        title="Fartsgrense"
                        prefix={<span>v</span>}
                        sufix='km/t'
                        type='number'
                        min={calculationLimits.minSpeed}
                        max={calculationLimits.maxSpeed}
                        error={calculatedState.input?.speedlimit?.severity === 'error' ?? null}
                        warning={calculatedState.input?.speedlimit?.severity === 'warning' ?? null}
                        value={rawInput.speedlimit}
                        onChange={(e) => setRawInput(prev => ({...prev, speedlimit: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, speedlimit: e.target.value}))}
                    />
                    <Input
                        title="Stigning på hovedvegen"
                        prefix={<span>s</span>}
                        sufix='m/m'
                        type='number'
                        min={calculationLimits.minLongitudalSlope}
                        max={calculationLimits.maxLongitudalSlope}
                        step={0.01}
                        error={calculatedState.input?.slope?.severity === 'error' ?? null}
                        warning={calculatedState.input?.slope?.severity === 'warning' ?? null}
                        value={rawInput.slope}
                        onChange={(e) => setRawInput(prev => ({...prev, slope: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, slope: e.target.value}))}
                    />
                    <Input
                        title="Gjennomgående trafikk"
                        prefix={<span>Q<sub>C, r</sub></span>}
                        sufix='kjt/t'
                        type='number'
                        error={calculatedState.input?.trafficThrough?.severity === 'error' ?? null}
                        warning={calculatedState.input?.trafficThrough?.severity === 'warning' ?? null}
                        value={rawInput.trafficThrough}
                        onChange={(e) => setRawInput(prev => ({...prev, trafficThrough: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, trafficThrough: e.target.value}))}
                    />
                    <Input
                        title="Svingende trafikk"
                        prefix={<span>Q<sub>C, v</sub></span>}
                        sufix='kjt/t'
                        type='number'
                        error={calculatedState.input?.trafficTurning?.severity === 'error' ?? null}
                        warning={calculatedState.input?.trafficTurning?.severity === 'warning' ?? null}
                        value={rawInput.trafficTurning}
                        onChange={(e) => setRawInput(prev => ({...prev, trafficTurning: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, trafficTurning: e.target.value}))}
                    />
                    <Input
                        title="Motgående trafikk"
                        prefix={<span>Q<sub>A</sub></span>}
                        sufix='kjt/t'
                        type='number'
                        error={calculatedState.input?.trafficOncoming?.severity === 'error' ?? null}
                        warning={calculatedState.input?.trafficOncoming?.severity === 'warning' ?? null}
                        value={rawInput.trafficOncoming}
                        onChange={(e) => setRawInput(prev => ({...prev, trafficOncoming: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, trafficOncoming: e.target.value}))}
                    />
                    <Input
                        title="Andel tungtrafikk"
                        prefix={<span>%tt</span>}
                        sufix='%'
                        type='number'
                        error={calculatedState.input?.heavyPercentage?.severity === 'error' ?? null}
                        warning={calculatedState.input?.heavyPercentage?.severity === 'warning' ?? null}
                        value={rawInput.heavyPercentage}
                        onChange={(e) => setRawInput(prev => ({...prev, heavyPercentage: e.target.value}))}
                        onBlur={(e) => setCalculationInput(prev => ({...prev, heavyPercentage: e.target.value}))}
                    />
                </ToolsContainer>
            </ToolsColumn>
            <ToolsColumn className='outputColumn'>
                <CalculationOutput
                    title="Lengde på parallelfelt"
                    prefix={<span>L<sub>1</sub></span>}
                    value={calculatedState.output?.turningLaneLength?.value?.toFixed(0) ?? '--'}
                    error={calculatedState.output?.turningLaneLength?.severity === 'error'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på overgangsstrekning"
                    prefix={<span>L<sub>2</sub></span>}
                    value={calculatedState.output?.transitionLength?.value?.toFixed(0) ?? '--'}
                    error={calculatedState.output?.transitionLength?.severity === 'error'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på retardasjonsstrekning"
                    prefix={<span>L<sub>r</sub></span>}
                    value={calculatedState.output?.retardtionLength?.value?.toFixed(0) ?? '--'}
                    error={calculatedState.output?.retardtionLength?.severity === 'error'}
                    sufix='m'
                />
                <CalculationOutput
                    title="Lengde på kømagasin"
                    prefix={<span>L<sub>kø</sub></span>}
                    value={calculatedState.output?.queueLength?.value?.toFixed(0) ?? '--'}
                    error={calculatedState.output?.queueLength?.severity === 'error'}
                    warning={calculatedState.output?.queueLength?.severity === 'warning'}
                    sufix='m'
                />
            </ToolsColumn>
            <ToolsColumn className='infoColumn'>
                <ContentContainer className='crossingTypeContainer'>
                    <LeftTurnCrossing/>
                </ContentContainer>
            </ToolsColumn>
        </StyledLeftturn>
    )
}

const StyledLeftturn = styled.div`
    display: grid;
    flex: 1;
    width: 100%;
    min-height: 0;

    @media (min-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr 2fr;
        grid-template-rows: 1fr;
        overflow: hidden;

        > div
        {
            
            min-height: 0;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.xl})
    {
        grid-template-columns: 1fr 1fr;
        grid-template-rows: min-content min-content;

        div.inputColumn, div.outputColumn
        {
            //max-height: 500px;
        }
        div.infoColumn
        {
            //max-height: 0.5fr;
            grid-column: 1/3;
        }
    }

    @media (max-width: ${({theme}) => theme.breakPoints.md})
    {
        grid-template-columns: 1fr;
        grid-template-rows: min-content min-content min-content;

        div.inputColumn
        {
            grid-column: 1;
            grid-row: 1/2;
        }
        div.outputColumn
        {
            grid-column: 1;
            grid-row: 2/3;
        }
        div.infoColumn
        {
            grid-column: 1;
            grid-row: 3/4;
        }
    }

    div.crossingTypeContainer
    {
        //background: ${({theme}) => theme.colors.neutralPrimaryBorder};

        svg
        {
            width: 100%;
            height: auto;
        }
    }
`